import React, { useEffect, useState } from 'react';
import { moneyMarketApi, counterpartyApi } from '../services/api';
import DealSlipViewModal from '../components/DealSlipViewModal';

export default function BackOfficeFinalMoneyMarketBlotter() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [counterparties, setCounterparties] = useState([]);
  const [rejectModal, setRejectModal] = useState({ open: false, deal: null, comment: '' });
  const [viewModal, setViewModal] = useState({ open: false, deal: null });
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      moneyMarketApi.getAll({ limit: 20 }),
      counterpartyApi.getAll()
    ])
      .then(([allDeals, cps]) => {
        // Only show deals at back_office_final and pending
        const filtered = allDeals.filter(
          d => d.current_approval_level === 'back_office_final' && d.status === 'pending'
        );
        setDeals(filtered);
        setCounterparties(cps);
      })
      .catch(() => setError('Failed to fetch deals'))
      .finally(() => setLoading(false));
  }, []);

  function getCounterpartyName(id) {
    const cp = counterparties.find(c => c.id === id);
    return cp ? cp.name : id;
  }

  async function handleApprove(deal) {
    // Final approval - update to final_approved
    await moneyMarketApi.update(deal.deal_number, {
      status: 'final_approved',
      current_approval_level: 'final_approved',
      authorized_by: user.username,
      authorized_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
    });
    // Refresh
    const allDeals = await moneyMarketApi.getAll({ limit: 20 });
    setDeals(allDeals.filter(d => d.current_approval_level === 'back_office_final' && d.status === 'pending'));
  }

  function openRejectModal(deal) {
    setRejectModal({ open: true, deal, comment: '' });
  }

  async function handleReject() {
    const { deal, comment } = rejectModal;
    if (!comment.trim()) return;
    await moneyMarketApi.update(deal.deal_number, {
      status: 'rejected',
      comment,
      current_approval_level: 'front_office', // Reset to front office so it goes back to creator
      authorized_by: user.username,
      authorized_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
    });
    setRejectModal({ open: false, deal: null, comment: '' });
    // Refresh
    const allDeals = await moneyMarketApi.getAll({ limit: 20 });
    setDeals(allDeals.filter(d => d.current_approval_level === 'back_office_final' && d.status === 'pending'));
  }

  function openViewModal(deal) {
    setViewModal({ open: true, deal });
  }

  function closeViewModal() {
    setViewModal({ open: false, deal: null });
  }

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (!isFinite(num)) return '';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="page-container sister-blotter">
      <h2>Back Office Final Money Market Blotter</h2>
      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div className="error-msg">{error}</div>
      ) : deals.length === 0 ? (
        <div>No deals found for final authorization.</div>
      ) : (
        <table className="table table-striped" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Deal Number</th>
              <th>Trade Date</th>
              <th>Counterparty</th>
              <th>Product Type</th>
              <th>Principal</th>
              <th>Status</th>
              <th>Approval Level</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {deals.map(deal => (
              <tr key={deal.deal_number}>
                <td>{deal.deal_number}</td>
                <td>{deal.trade_date ? deal.trade_date.slice(0, 10) : ''}</td>
                <td>{getCounterpartyName(deal.counterparty_id)}</td>
                <td>{deal.product_type}</td>
                <td>{deal.principal_amount}</td>
                <td>{
                  deal.status === 'final_approved' || deal.current_approval_level === 'final_approved'
                    ? 'Final Approved'
                    : deal.status || 'pending'
                }</td>
                <td>{
                  deal.current_approval_level === 'final_approved'
                    ? 'Final Approved'
                    : deal.current_approval_level
                }</td>
                <td>
                  <button className="view-details-btn" onClick={() => openViewModal(deal)} style={{marginRight:8}}>View</button>
                  <button className="approve-btn" onClick={() => handleApprove(deal)} style={{marginRight:8}}>Approve</button>
                  <button className="reject-btn" onClick={() => openRejectModal(deal)} style={{marginRight:8}}>Reject</button>
                  <button className="voucher-btn" onClick={async () => {
                    try {
                      const pdfBlob = await moneyMarketApi.fetchVoucherPdf(deal.deal_number);
                      const url = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `DealVoucher_${deal.deal_number}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.parentNode.removeChild(link);
                      window.URL.revokeObjectURL(url);
                    } catch (e) {
                      alert('Failed to download voucher PDF');
                    }
                  }} style={{marginRight:8}}>Download Voucher</button>
                  {deal.status === 'rejected' && deal.comment && (
                    <div style={{color:'#ef4444', fontSize:'0.85em', marginTop:4}}>Reason: {deal.comment}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rejectModal.open && (
        <div className="modal-overlay" onClick={() => setRejectModal({ open: false, deal: null, comment: '' })}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Reject Deal</h3>
            <textarea
              rows={4}
              style={{width: '100%', marginBottom: 16}}
              placeholder="Enter rejection reason/message"
              value={rejectModal.comment}
              onChange={e => setRejectModal({ ...rejectModal, comment: e.target.value })}
            />
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <button onClick={() => setRejectModal({ open: false, deal: null, comment: '' })}>Cancel</button>
              <button className="reject-btn" style={{background:'#c62828',color:'#fff'}} onClick={handleReject}>Reject</button>
            </div>
          </div>
        </div>
      )}
      {viewModal.open && viewModal.deal && (
        <DealSlipViewModal
          dealType="MoneyMarket"
          transaction={{
            ...viewModal.deal,
            counterparty_name: (() => {
              const cp = counterparties.find(c => String(c.id) === String(viewModal.deal.counterparty_id));
              return cp ? (cp.short_name || cp.long_name || cp.name) : viewModal.deal.counterparty_id;
            })(),
          }}
          onClose={closeViewModal}
        />
      )}
    </div>
  );
} 